import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { HiOutlineX } from 'react-icons/hi';
import { MdEdit, MdDelete } from 'react-icons/md';
import toast from 'react-hot-toast';
import { BrandNote, submitBrandNote, updateBrandNote, deleteBrandNote, fetchBrandAuditLog } from '../../services/brandService';
import { StoreNote, fetchStoreNotes, submitStoreNote, updateStoreNote, deleteStoreNote, fetchStoreAuditLog } from '../../services/brandSitesService';
import { NoteAuditLogEntry } from '../../services/auditLogService';
import { useAuthStore } from '../../stores';
import ThemeLoader from '../ThemeLoader/ThemeLoader';
import './BrandNotesDialog.css';

interface Comment {
  id: string;
  text: string;
  timestamp: number;
  brandName: string;
  userId: string | null;
  userName: string;
}

interface BrandNotesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  brandName: string;
  notes?: BrandNote[] | StoreNote[] | null;
  isLoading?: boolean;
  onNoteAdded?: () => void;
  poiId?: string; // For store notes
  isStoreNote?: boolean; // Flag to indicate if this is store note
}

// Convert database notes to Comment format
const convertNotesToComments = (notes: (BrandNote | StoreNote)[], brandName: string): Comment[] => {
  return notes.map(note => ({
    id: note.id,
    text: note.comment,
    timestamp: note.created_at ? new Date(note.created_at).getTime() : Date.now(),
    brandName,
    userId: note.user?.id ?? null,
    userName: (note.user && note.user.name) ? note.user.name : 'Unknown User'
  }));
};

const AUDIT_ACTION_LABELS: Record<NoteAuditLogEntry['action'], string> = {
  note_added: 'added a note',
  note_edited: 'edited a note',
  note_deleted: 'deleted a note'
};

export default function BrandNotesDialog({
  isOpen,
  onClose,
  brandName,
  notes = [],
  isLoading = false,
  onNoteAdded,
  poiId,
  isStoreNote = false,
}: BrandNotesDialogProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [isLoadingNotes, setIsLoadingNotes] = useState(isLoading);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'notes' | 'history'>('notes');
  const [auditLog, setAuditLog] = useState<NoteAuditLogEntry[]>([]);
  const [isLoadingAuditLog, setIsLoadingAuditLog] = useState(false);
  const wasOpenRef = useRef(false);
  const hasChangesRef = useRef(false);

  const currentUser = useAuthStore((s) => s.user);
  const currentUserId = (currentUser as any)?.id ?? null;

  const refreshAuditLog = useCallback(() => {
    const fetcher = isStoreNote
      ? (poiId ? fetchStoreAuditLog(poiId) : Promise.resolve([]))
      : fetchBrandAuditLog(brandName);
    setIsLoadingAuditLog(true);
    fetcher
      .then(setAuditLog)
      .finally(() => setIsLoadingAuditLog(false));
  }, [isStoreNote, poiId, brandName]);

  // Load comments when dialog opens or when notes change
  useEffect(() => {
    setIsLoadingNotes(isLoading);
  }, [isLoading]);

  // Load notes when dialog first opens (for store notes, fetch fresh from API)
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Dialog just opened
      hasChangesRef.current = false;
      setActiveTab('notes');
      refreshAuditLog();

      if (isStoreNote && poiId) {
        // For store notes, always fetch fresh from API to avoid stale cache
        setIsLoadingNotes(true);
        fetchStoreNotes(poiId).then(freshNotes => {
          if (freshNotes && freshNotes.length > 0) {
            setComments(convertNotesToComments(freshNotes, brandName));
          } else {
            setComments([]);
          }
          setIsLoadingNotes(false);
          // Sync fresh notes back to parent so badge counts stay accurate
          onNoteAdded?.();
        }).catch(() => {
          // Fallback to prop if fetch fails
          if (notes && notes.length > 0) {
            setComments(convertNotesToComments(notes, brandName));
          } else {
            setComments([]);
          }
          setIsLoadingNotes(false);
        });
      }
    }
    if (!isOpen && wasOpenRef.current && hasChangesRef.current) {
      // Dialog just closed and had changes — notify parent to refresh counts
      onNoteAdded?.();
      hasChangesRef.current = false;
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, brandName, notes, onNoteAdded, isStoreNote, poiId, refreshAuditLog]);

  // For brand notes, sync from notes prop whenever it changes (handles async fetch)
  useEffect(() => {
    if (isOpen && !isStoreNote && !hasChangesRef.current) {
      if (notes && notes.length > 0) {
        setComments(convertNotesToComments(notes, brandName));
      } else if (!isLoading) {
        setComments([]);
      }
    }
  }, [isOpen, isStoreNote, notes, brandName, isLoading]);

  // On a failed/ambiguous submit, refetch store notes and check whether our note
  // actually landed on the server — a request can error (or return null) after the
  // write has already committed. Adopts the fresh server list and returns true if
  // the just-submitted note is found. Only store notes have a refetch path here.
  const recoverSubmittedNote = async (text: string, userId: string): Promise<boolean> => {
    if (!isStoreNote || !poiId) return false;
    const existingIds = new Set(comments.map(c => c.id));
    try {
      const freshNotes = await fetchStoreNotes(poiId);
      if (!freshNotes || freshNotes.length === 0) return false;
      // Match on text + author among notes we didn't already have (avoids a
      // false positive when an identical note existed before this submit).
      const landed = freshNotes.some(
        n => n.comment === text && (n.user?.id ?? null) === userId && !existingIds.has(n.id)
      );
      setComments(convertNotesToComments(freshNotes, brandName));
      return landed;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = newComment.trim();
    if (!text) return;

    setIsSubmitting(true);

    try {
      // Get userId from authenticated user
      const authUser = useAuthStore.getState().user;
      const userId = (authUser as any)?.id;

      if (!userId) {
        console.error('User not authenticated');
        toast.error('You must be signed in to add a note.');
        return;
      }

      // Submit to appropriate API based on note type
      let result;
      if (isStoreNote && poiId) {
        result = await submitStoreNote(poiId, userId, text);
      } else {
        result = await submitBrandNote(brandName, userId, text);
      }

      if (result) {
        // Add the new note directly to comments state using functional update to avoid stale closure
        const newCommentData: Comment = {
          id: result.id,
          text: result.comment,
          timestamp: result.created_at ? new Date(result.created_at).getTime() : Date.now(),
          brandName,
          userId: result.user?.id ?? userId ?? null,
          userName: result.user?.name || (authUser as any)?.name || 'Unknown User'
        };
        setComments(prev => [newCommentData, ...prev]);
        setNewComment('');
        hasChangesRef.current = true;
        refreshAuditLog();
        // Immediately notify parent to update badge count
        onNoteAdded?.();
        return;
      }

      // Submit returned null — the write may still have landed. Refetch and check.
      console.error('Submit returned null, refetching notes from server');
      if (await recoverSubmittedNote(text, userId)) {
        setNewComment('');
        hasChangesRef.current = true;
        refreshAuditLog();
        onNoteAdded?.();
      } else {
        // Couldn't confirm the note was saved — keep the draft so the user can retry
        toast.error('Failed to add note. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting note:', error);
      // The request errored, but the mutation may have committed server-side. Refetch to check.
      const userId = (useAuthStore.getState().user as any)?.id;
      if (userId && await recoverSubmittedNote(text, userId)) {
        setNewComment('');
        hasChangesRef.current = true;
        refreshAuditLog();
        onNoteAdded?.();
      } else {
        // Couldn't confirm the note was saved — keep the draft so the user can retry
        toast.error('Failed to add note. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      // Delete from API using appropriate function
      if (isStoreNote) {
        await deleteStoreNote(commentId);
      } else {
        await deleteBrandNote(commentId);
      }
      // Success — remove from local state
      setComments(prev => prev.filter(c => c.id !== commentId));
      hasChangesRef.current = true;
      refreshAuditLog();
      // Immediately notify parent to update badge count
      onNoteAdded?.();
    } catch (error) {
      console.error('Error deleting note:', error);
      // Delete failed on the server — keep the note in the UI and inform the user
      toast.error('Failed to delete note. Please try again.');
    }
  };

  const handleEdit = (commentId: string, currentText: string) => {
    setEditingCommentId(commentId);
    setEditText(currentText);
  };

  const handleSaveEdit = async (commentId: string) => {
    if (!editText.trim()) return;

    try {
      // Update via API using appropriate function
      const updated = isStoreNote ? await updateStoreNote(commentId, editText.trim()) : await updateBrandNote(commentId, editText.trim());

      if (!updated) {
        // Update returned nothing — treat as failure, keep editor open with the draft
        toast.error('Failed to update note. Please try again.');
        return;
      }

      // Success — update local state with response from API
      setComments(prev => prev.map(c =>
        c.id === commentId
          ? {
              ...c,
              text: updated.comment,
              timestamp: updated.updated_at ? new Date(updated.updated_at).getTime() : c.timestamp
            }
          : c
      ));

      setEditingCommentId(null);
      setEditText('');
      hasChangesRef.current = true;
      refreshAuditLog();
    } catch (error) {
      console.error('Error updating note:', error);
      // Update failed on the server — keep edit mode open with the user's draft and inform them
      toast.error('Failed to update note. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditText('');
  };

  const formatFullDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return ReactDOM.createPortal(
    <div className="notes-dialog-overlay" onClick={handleOverlayClick}>
      <div className="notes-dialog">
        <div className="notes-dialog-header">
          <h2>Notes for {brandName}</h2>
          <button onClick={onClose} className="notes-close-btn">
            <HiOutlineX />
          </button>
        </div>

        <div className="notes-dialog-description">
          Add your notes here. Only you and the development team can view these notes.
        </div>

        <div className="notes-tabs-bar">
          <button
            type="button"
            className={`notes-tab ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            Notes
          </button>
          <button
            type="button"
            className={`notes-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
        </div>

        {activeTab === 'history' ? (
          <div className="notes-dialog-body">
            {isLoadingAuditLog && <ThemeLoader text="Loading history" />}

            {!isLoadingAuditLog && auditLog.length > 0 && (
              <div className="notes-history-list">
                {auditLog.map((entry) => (
                  <div key={entry.id} className="notes-history-item">
                    <div className="notes-history-header">
                      <span className="notes-history-user">{entry.user?.name ?? 'Unknown User'}</span>
                      <span className="notes-history-action">{AUDIT_ACTION_LABELS[entry.action]}</span>
                    </div>
                    {entry.action === 'note_edited' && (
                      <p className="notes-history-value">
                        <span className="notes-history-old-value">{entry.oldValue}</span>
                        {' → '}
                        <span className="notes-history-new-value">{entry.newValue}</span>
                      </p>
                    )}
                    {entry.action === 'note_added' && (
                      <p className="notes-history-value">{entry.newValue}</p>
                    )}
                    {entry.action === 'note_deleted' && (
                      <p className="notes-history-value notes-history-old-value">{entry.oldValue}</p>
                    )}
                    <div className="notes-comment-timestamp">
                      {formatFullDate(new Date(entry.timestamp).getTime())}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!isLoadingAuditLog && auditLog.length === 0 && (
              <div className="notes-empty-state">
                No history yet for {isStoreNote ? "this store's" : "this brand's"} notes.
              </div>
            )}
          </div>
        ) : (
        <div className="notes-dialog-body">
          {/* Comment form */}
          <form onSubmit={handleSubmit} className="notes-form">
            <textarea
              placeholder="Type your note here..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="notes-textarea"
              disabled={isLoadingNotes}
            />
            <div className="notes-form-footer">
              <button
                type="submit"
                disabled={!newComment.trim() || isLoadingNotes || isSubmitting}
                className="notes-submit-btn"
              >
                {isSubmitting ? 'Submitting...' : 'Add Note'}
              </button>
            </div>
          </form>

          {/* Loading indicator */}
          {isLoadingNotes && <ThemeLoader text="Loading notes" />}

          {/* Comments list */}
          {!isLoadingNotes && comments.length > 0 && (
            <div className="notes-comments-list">
              <div className="notes-comments-header">
                Notes ({comments.length})
              </div>
              {comments.map((comment) => (
                <div key={comment.id} className="notes-comment-card">
                  {editingCommentId === comment.id ? (
                    <div className="notes-edit-mode">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="notes-textarea"
                      />
                      <div className="notes-edit-buttons">
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          className="notes-btn-cancel"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSaveEdit(comment.id)}
                          className="notes-btn-save"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="notes-comment-header">
                        <div className="notes-user-info">
                          <div className="notes-user-avatar">
                            {(comment.userName || 'U').charAt(0).toUpperCase()}
                          </div>
                          <span className="notes-user-email">
                            {comment.userName || 'Unknown User'}
                          </span>
                        </div>
                        {comment.userId === currentUserId && (
                          <div className="notes-comment-actions">
                            <button
                              type="button"
                              onClick={() =>
                                handleEdit(comment.id, comment.text)
                              }
                              className="notes-action-btn notes-edit-btn"
                              title="Edit"
                            >
                              <MdEdit />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(comment.id)}
                              className="notes-action-btn notes-delete-btn"
                              title="Delete"
                            >
                              <MdDelete />
                            </button>
                          </div>
                        )}
                      </div>

                      <p className="notes-comment-text">{comment.text}</p>

                      <div className="notes-comment-timestamp">
                        {formatFullDate(comment.timestamp)}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {!isLoadingNotes && comments.length === 0 && (
            <div className="notes-empty-state">
              No notes added yet. Share your thoughts above.
            </div>
          )}
        </div>
        )}
      </div>
    </div>,
    document.body
  );
}
